import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Trophy, ArrowRight, ArrowLeft, CheckCircle2, Users, 
  Share2, Building2, ChevronRight, Copy, Check,
  Target, Briefcase, MessageSquare, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CONFIGURATION
// ============================================
const QUALIFICATION_THRESHOLD = 50;
const CAMPAIGN_NAME = "Dehradun Career Readiness Index 2026";

// ============================================
// CAREER READINESS QUESTIONS (Reused from main check)
// ============================================
const questions = [
  {
    id: 'career_direction',
    category: 'Career Direction',
    icon: Target,
    question: 'How clear are you about the career path you want to pursue?',
    options: [
      { value: 'very_clear', label: 'Very clear - I know exactly what I want', score: 100 },
      { value: 'somewhat_clear', label: 'Somewhat clear - I have a general idea', score: 70 },
      { value: 'exploring', label: 'Still exploring different options', score: 40 },
      { value: 'no_idea', label: 'No idea yet', score: 10 },
    ]
  },
  {
    id: 'profile_strength',
    category: 'Profile Strength',
    icon: Briefcase,
    question: 'How would you rate your resume/CV and LinkedIn profile?',
    options: [
      { value: 'excellent', label: 'Excellent - Updated and optimized', score: 100 },
      { value: 'good', label: 'Good - Decent but needs some work', score: 70 },
      { value: 'basic', label: 'Basic - Just the essentials', score: 40 },
      { value: 'none', label: 'Haven\'t created one yet', score: 10 },
    ]
  },
  {
    id: 'interview_readiness',
    category: 'Interview Readiness',
    icon: MessageSquare,
    question: 'How prepared are you for job interviews?',
    options: [
      { value: 'very_prepared', label: 'Very prepared - Done mock interviews', score: 100 },
      { value: 'somewhat_prepared', label: 'Somewhat prepared - Know the basics', score: 70 },
      { value: 'learning', label: 'Still learning - Need more practice', score: 40 },
      { value: 'not_prepared', label: 'Not prepared at all', score: 10 },
    ]
  },
  {
    id: 'job_search',
    category: 'Job Search Strategy',
    icon: Search,
    question: 'How active is your job search strategy?',
    options: [
      { value: 'very_active', label: 'Very active - Applying regularly with a plan', score: 100 },
      { value: 'somewhat_active', label: 'Somewhat active - Applying occasionally', score: 70 },
      { value: 'just_starting', label: 'Just starting to look', score: 40 },
      { value: 'not_started', label: 'Haven\'t started yet', score: 10 },
    ]
  },
];

// ============================================
// GA4 TRACKING
// ============================================
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, { ...params, campaign: 'dcri_2026' });
  }
};

// ============================================
// MAIN COMPONENT
// ============================================
export const DehradunCheck = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('registration'); // registration, assessment, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [collegeStats, setCollegeStats] = useState({ count: 0, qualified: false });
  const [copied, setCopied] = useState(false);
  
  // Registration form data
  const [formData, setFormData] = useState({
    college: '',
    course: '',
    year: '',
    name: '',
    email: '',
    phone: '',
    referral_code: searchParams.get('ref') || '',
  });

  // UTM parameters
  const utmParams = {
    utm_source: searchParams.get('utm_source') || 'dehradun_campaign',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || 'dcri2026',
    utm_content: searchParams.get('utm_content') || '',
    ref: searchParams.get('ref') || '',
  };

  useEffect(() => {
    document.title = `${CAMPAIGN_NAME} | Elevate Careers`;
    trackEvent('dcri_check_started');
  }, []);

  // Calculate score
  const calculateScore = () => {
    let totalScore = 0;
    let answeredCount = 0;
    
    questions.forEach(q => {
      if (answers[q.id]) {
        const option = q.options.find(o => o.value === answers[q.id]);
        if (option) {
          totalScore += option.score;
          answeredCount++;
        }
      }
    });
    
    return answeredCount > 0 ? Math.round(totalScore / answeredCount) : 0;
  };

  // Get score band
  const getScoreBand = (score) => {
    if (score >= 80) return { label: 'Excellent', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (score >= 60) return { label: 'Good', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (score >= 40) return { label: 'Developing', color: 'text-amber-600', bg: 'bg-amber-100' };
    return { label: 'Getting Started', color: 'text-red-600', bg: 'bg-red-100' };
  };

  // Handle registration submit
  const handleRegistrationSubmit = (e) => {
    e.preventDefault();
    trackEvent('dcri_registration_completed', { college: formData.college });
    setStep('assessment');
  };

  // Handle answer selection
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      }
    }, 300);
  };

  // Handle assessment completion
  const handleComplete = async () => {
    setIsSubmitting(true);
    const score = calculateScore();
    
    try {
      const payload = {
        ...formData,
        answers,
        score,
        utm_params: utmParams,
        source: 'dcri_2026',
      };

      const res = await fetch(`${API_URL}/api/dcri/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      
      if (data.success) {
        setResult({
          score,
          band: getScoreBand(score),
          categoryScores: questions.map(q => ({
            category: q.category,
            score: q.options.find(o => o.value === answers[q.id])?.score || 0
          }))
        });
        setCollegeStats({
          count: data.college_count || 1,
          qualified: (data.college_count || 1) >= QUALIFICATION_THRESHOLD
        });
        trackEvent('dcri_assessment_completed', { score, college: formData.college });
        setStep('result');
      }
    } catch (err) {
      console.error('Submit error:', err);
      // Still show result even if API fails
      setResult({
        score,
        band: getScoreBand(score),
        categoryScores: questions.map(q => ({
          category: q.category,
          score: q.options.find(o => o.value === answers[q.id])?.score || 0
        }))
      });
      setStep('result');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate share URL
  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/dehradun?ref=${formData.email.split('@')[0]}_${Date.now().toString(36)}`;
  };

  // Copy share link
  const copyShareLink = () => {
    navigator.clipboard.writeText(generateShareUrl());
    setCopied(true);
    trackEvent('dcri_share_link_copied');
    setTimeout(() => setCopied(false), 2000);
  };

  // Share on WhatsApp
  const shareOnWhatsApp = () => {
    const text = `I just took the Career Readiness Check for ${CAMPAIGN_NAME}! 🎯\n\nMy score: ${result?.score}%\n\nRepresent our college and help us climb the leaderboard:\n${generateShareUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    trackEvent('dcri_shared_whatsapp');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ============================================
          HEADER
          ============================================ */}
      <header className="py-3 px-4 bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/dehradun" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium">{CAMPAIGN_NAME}</span>
          </div>
        </div>
      </header>

      {/* ============================================
          REGISTRATION STEP
          ============================================ */}
      {step === 'registration' && (
        <div className="max-w-lg mx-auto px-4 py-8">
          {/* Campaign Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Trophy className="w-4 h-4" />
              {CAMPAIGN_NAME}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Represent Your College
            </h1>
            <p className="text-slate-600">
              3 minutes. Get your score. Help your college rank.
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegistrationSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">College Name *</label>
                <Input
                  required
                  value={formData.college}
                  onChange={(e) => setFormData({ ...formData, college: e.target.value })}
                  placeholder="e.g., Graphic Era University"
                  className="w-full"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Course *</label>
                  <Input
                    required
                    value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    placeholder="e.g., B.Tech CSE"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Year *</label>
                  <select
                    required
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="1st Year">1st Year</option>
                    <option value="2nd Year">2nd Year</option>
                    <option value="3rd Year">3rd Year</option>
                    <option value="4th Year">4th Year</option>
                    <option value="5th Year">5th Year</option>
                    <option value="Graduate">Graduate</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@college.edu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">WhatsApp Number *</label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="10-digit mobile number"
                />
              </div>

              {formData.referral_code && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                  <span className="text-emerald-700">Referral code applied: </span>
                  <strong className="text-emerald-800">{formData.referral_code}</strong>
                </div>
              )}
            </div>

            <Button 
              type="submit"
              className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-xl font-semibold"
            >
              Start Career Readiness Check
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>

            <p className="text-xs text-slate-500 text-center mt-4">
              Your responses are confidential. Only aggregate data is used for rankings.
            </p>
          </form>
        </div>
      )}

      {/* ============================================
          ASSESSMENT STEP
          ============================================ */}
      {step === 'assessment' && (
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* College Badge */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-medium text-[#0b1f4d]">
                Representing: <strong>{formData.college}</strong>
              </span>
            </div>
            <span className="text-xs text-teal-600 font-medium">
              {currentQuestion + 1} of {questions.length}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-2 bg-slate-200 rounded-full mb-8">
            <div 
              className="h-full bg-teal-600 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              {(() => {
                const Icon = questions[currentQuestion].icon;
                return <Icon className="w-6 h-6 text-teal-600" />;
              })()}
              <span className="text-sm font-medium text-teal-600 uppercase tracking-wide">
                {questions[currentQuestion].category}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">
              {questions[currentQuestion].question}
            </h2>

            <div className="space-y-3">
              {questions[currentQuestion].options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(questions[currentQuestion].id, option.value)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    answers[questions[currentQuestion].id] === option.value
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className="font-medium text-slate-800">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
              className="px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {currentQuestion === questions.length - 1 ? (
              <Button
                onClick={handleComplete}
                disabled={!answers[questions[currentQuestion].id] || isSubmitting}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8"
              >
                {isSubmitting ? 'Submitting...' : 'See My Result'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={!answers[questions[currentQuestion].id]}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ============================================
          RESULT STEP
          ============================================ */}
      {step === 'result' && result && (
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Success Badge */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              You're Representing {formData.college}!
            </h1>
            <p className="text-slate-600">
              Your response has been recorded for the {CAMPAIGN_NAME}
            </p>
          </div>

          {/* Score Card */}
          <div className="bg-gradient-to-br from-teal-600 to-[#0b1f4d] rounded-2xl p-6 sm:p-8 text-white mb-6">
            <div className="text-center">
              <p className="text-teal-200 text-sm font-medium mb-2">Your Career Readiness Score</p>
              <div className="text-6xl sm:text-7xl font-bold mb-2">{result.score}%</div>
              <div className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold ${result.band.bg} ${result.band.color}`}>
                {result.band.label}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              {result.categoryScores.map((cat, idx) => (
                <div key={idx} className="bg-white/10 rounded-lg p-3">
                  <p className="text-xs text-teal-200 mb-1">{cat.category}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-white/20 rounded-full">
                      <div 
                        className="h-full bg-white rounded-full"
                        style={{ width: `${cat.score}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold">{cat.score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* College Progress */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-6 h-6 text-teal-600" />
              <h3 className="font-bold text-slate-900">College Progress</h3>
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-600">{formData.college}</span>
              <span className="font-bold text-teal-600">
                {collegeStats.count} / {QUALIFICATION_THRESHOLD} students
              </span>
            </div>
            
            <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden mb-3">
              <div 
                className={`h-full rounded-full transition-all ${
                  collegeStats.qualified ? 'bg-emerald-500' : 'bg-teal-500'
                }`}
                style={{ width: `${Math.min((collegeStats.count / QUALIFICATION_THRESHOLD) * 100, 100)}%` }}
              />
            </div>
            
            {collegeStats.qualified ? (
              <p className="text-sm text-emerald-600 font-medium">
                🎉 Your college has qualified for the leaderboard!
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                {QUALIFICATION_THRESHOLD - collegeStats.count} more students needed to qualify for the leaderboard
              </p>
            )}
          </div>

          {/* CTAs */}
          <div className="space-y-3">
            <Link to="/dehradun#leaderboard">
              <Button className="w-full bg-slate-800 hover:bg-slate-900 text-white py-4 rounded-xl font-semibold">
                <Trophy className="w-5 h-5 mr-2" />
                View Leaderboard
              </Button>
            </Link>

            <Button 
              onClick={shareOnWhatsApp}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl font-semibold"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share on WhatsApp
            </Button>

            <Button 
              onClick={copyShareLink}
              variant="outline"
              className="w-full py-4 rounded-xl font-semibold"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2 text-emerald-600" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copy Invite Link
                </>
              )}
            </Button>
          </div>

          {/* Invite Message */}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
            <Users className="w-8 h-8 text-amber-600 mx-auto mb-3" />
            <h3 className="font-bold text-slate-900 mb-2">Help Your College Climb!</h3>
            <p className="text-sm text-slate-600">
              Share with your batchmates. Every student who takes the check helps 
              your college's ranking on the {CAMPAIGN_NAME}.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <Link to="/dehradun" className="text-teal-600 hover:text-teal-700 font-medium">
              ← Back to Campaign Page
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default DehradunCheck;
